import { Business, Person, Service } from "@turizi/shared";
import { useGlobalSearch } from "@/hooks/api-hooks";
import { Modal, TextInput, Button } from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useState, useRef } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { navigate, SERVICE_PATH, PAYMENTS_PATH, SERVICE_BOOKING_PATH, RESERVATION_PATH, BUSINESS_PATH, PERSON_PATH } from "@/lib/navigation";
import { SearchReservationRow, SearchPersonRow, SearchBusinessRow, SearchServiceRow, SearchMenuLinkRow, SearchPaymentRow, SearchServiceBookingRow } from "./search-rows";
import { getMenuItems } from "./left-menu";

const rowRenderers = {
    'menu-link': (item: any, ref: any, highlighted: boolean, onClick: () => void) => <SearchMenuLinkRow ref={ref} menuLink={item} highlighted={highlighted} onClick={onClick} />,
    'business': (item: any, ref: any, highlighted: boolean, onClick: () => void) => <SearchBusinessRow ref={ref} business={item} highlighted={highlighted} onClick={onClick} />,
    'person': (item: any, ref: any, highlighted: boolean, onClick: () => void) => <SearchPersonRow ref={ref} person={item} highlighted={highlighted} onClick={onClick} />,
    'reservation': (item: any, ref: any, highlighted: boolean, onClick: () => void) => <SearchReservationRow ref={ref} reservation={item} highlighted={highlighted} onClick={onClick} />,
    'service': (item: any, ref: any, highlighted: boolean, onClick: () => void) => <SearchServiceRow ref={ref} service={item} highlighted={highlighted} onClick={onClick} />,
    'payment': (item: any, ref: any, highlighted: boolean, onClick: () => void) => <SearchPaymentRow ref={ref} payment={item} highlighted={highlighted} onClick={onClick} />,
    'service-booking': (item: any, ref: any, highlighted: boolean, onClick: () => void) => <SearchServiceBookingRow ref={ref} booking={item} highlighted={highlighted} onClick={onClick} />,
};


interface Props {
    onSelect: (item: Business | Person | Service | any) => void;
    className?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    variant?: 'default' | 'unstyled';
    isCollapsed?: boolean;
}


export default function GlobalSearch({
    variant = 'default',
    onSelect,
    size = 'sm',
    isCollapsed = false,
}: Props) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
    const [opened, setOpened] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Load Data
    const {
        data: searchResponse,
        isLoading
    } = useGlobalSearch(
        debouncedSearchQuery,
        undefined as any,
        { enabled: opened && debouncedSearchQuery.length >= 2 }
    );

    const query = debouncedSearchQuery.trim();

    const resultData = searchResponse?.data || {};
    const rawDataArray = Array.isArray(resultData) ? resultData : [];

    const businesses = rawDataArray.length > 0
        ? rawDataArray.filter((i: any) => i.type === 'business')
        : (resultData.businesses || []).map((i: any) => ({ ...i, type: 'business' }));

    const paxs = rawDataArray.length > 0
        ? rawDataArray.filter((i: any) => i.type === 'person')
        : (resultData.persons || []).map((i: any) => ({ ...i, type: 'person' }));

    const reservations = rawDataArray.length > 0
        ? rawDataArray.filter((i: any) => i.type === 'reservation')
        : (resultData.reservations || []).map((i: any) => ({ ...i, type: 'reservation' }));

    const services = rawDataArray.length > 0
        ? rawDataArray.filter((i: any) => i.type === 'service')
        : (resultData.services || []).map((i: any) => ({ ...i, type: 'service' }));

    const payments = rawDataArray.length > 0
        ? rawDataArray.filter((i: any) => i.type === 'payment')
        : (resultData.payments || []).map((i: any) => ({ ...i, type: 'payment' }));

    const serviceBookings = rawDataArray.length > 0
        ? rawDataArray.filter((i: any) => i.type === 'service-booking')
        : (resultData.bookings || []).map((i: any) => ({ ...i, type: 'service-booking' }));

    const menuLinks = getMenuItems(t)
        .filter((item) => {
            if ((item as any).divider || !item.label || !item.path) return false;
            if (!query) return false;
            return item.label.toLowerCase().includes(query.toLowerCase());
        })
        .map((item) => ({ ...item, type: 'menu-link' }));

    const displayData = [
        ...menuLinks,
        ...reservations,
        ...businesses,
        ...paxs,
        ...payments,
        ...services,
        ...serviceBookings,
    ];

    const dataGroups: Record<string, any[]> = {
        'menu-link': menuLinks,
        'reservation': reservations,
        'business': businesses,
        'person': paxs,
        'payment': payments,
        'service': services,
        'service-booking': serviceBookings,
    };

    const handleSelect = (item: any) => {
        setOpened(false);
        setSearchQuery('');
        if (item.type === 'menu-link') {
            navigate(item.path);
        } else if (item.type === 'reservation') {
            navigate(RESERVATION_PATH.replace(':id', item.id.toString()));
        } else if (item.type === 'service-booking') {
            navigate(SERVICE_BOOKING_PATH.replace(':id', item.id.toString()));
        } else if (item.type === 'person') {
            navigate(PERSON_PATH.replace(':id', item.id.toString()));
        } else if (item.type === 'business') {
            navigate(BUSINESS_PATH.replace(':id', item.id.toString()));
        } else if (item.type === 'service') {
            navigate(SERVICE_PATH.replace(':id', item.id.toString()));
        } else if (item.type === 'payment') {
            navigate(`${PAYMENTS_PATH}/${item.id}`);
        }
        if (onSelect) {
            onSelect(item);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!opened || displayData.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) => {
                    const next = prev < displayData.length - 1 ? prev + 1 : 0;
                    itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
                    return next;
                });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => {
                    const next = prev > 0 ? prev - 1 : displayData.length - 1;
                    itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
                    return next;
                });
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < displayData.length) {
                    handleSelect(displayData[highlightedIndex]);
                }
                break;
        }
    };

    return (
        <>
            <div onClick={() => {
                setSearchQuery('');
                setOpened(true);
            }}>
                <div
                    className={`
                        w-full max-w-[200px] sm:w-49    
                        cursor-pointer
                        rounded-full transition-all
                        overflow-hidden
                        text-${size}
                        ${variant === 'default' && 'bg-white border border-gray-200'}
                        hover:text-black
                        h-8 sm:h-9 px-2 sm:px-2.5
                        flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}
                    `}>
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <IconSearch size={18} stroke={2} className="text-gray-500 shrink-0" />
                        {!isCollapsed && (
                            <div className="text-xs sm:text-sm text-gray-400 truncate">
                                {searchQuery || t('search') + '...'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                yOffset="5vh"
                size="lg"
                radius="lg"
                withCloseButton={false}
                padding="sm"
            >
                <div className="overflow-hidden rounded-lg">
                    <div className="flex w-full gap-2 items-center text-sm p-2 border-b border-gray-100 bg-white">
                        <div className="pl-2">
                            <IconSearch size={18} stroke={2} className="text-gray-400" />
                        </div>
                        <TextInput
                            variant="unstyled"
                            autoFocus
                            size="md"
                            placeholder={t('search') + '...'}
                            className="flex-1 font-medium"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.currentTarget.value);
                                setHighlightedIndex(-1);
                            }}
                            onKeyDown={handleKeyDown}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                        />
                        <Button
                            variant="subtle"
                            color="gray"
                            size="xs"
                            loading={isLoading}
                            onClick={() => {
                                setOpened(false);
                                setSearchQuery('');
                            }}
                        >
                            <IconX size={18} stroke={2} />
                        </Button>
                    </div>
                    <div className="px-1.5 py-1 bg-white">
                        <div className="max-h-[620px] overflow-y-auto">
                            {displayData.map((item, index) => {
                                const highlighted = highlightedIndex === index;
                                const key = item.type === 'menu-link' ? `${item.type}-${item.path}` : `${item.type}-${item.id}`;
                                const ref = (el: HTMLDivElement) => { itemRefs.current[index] = el; };
                                const onClick = () => handleSelect(item);

                                const showHeader = index === 0 || displayData[index - 1].type !== item.type;

                                const renderRow = rowRenderers[item.type as keyof typeof rowRenderers];
                                const rowContent = renderRow ? renderRow(item, ref as any, highlighted, onClick) : null;

                                const typeToKey: Record<string, string> = {
                                    'menu-link': 'menu',
                                    'business': 'businesses',
                                    'person': 'paxs',
                                    'reservation': 'reservations',
                                    'service': 'services',
                                    'payment': 'payments',
                                    'service-booking': 'bookings',
                                };

                                return (
                                    <div key={key} className="flex flex-col">
                                        {showHeader && (
                                            <div className="px-2 py-0.5 flex items-center justify-between bg-gray-200 rounded-md mt-2 mb-1">
                                                <span className="text-[9px] font-medium uppercase tracking-wider text-gray-800">
                                                    {t(typeToKey[item.type] || item.type)}
                                                </span>
                                                <span className="text-[10px] font-medium text-gray-900">
                                                    {dataGroups[item.type]?.length || 0}
                                                </span>
                                            </div>
                                        )}
                                        {rowContent}
                                    </div>
                                );
                            })}

                            {displayData.length === 0 && !query && (
                                <div className="py-6 text-center text-xs text-gray-400 font-medium">
                                    {t('search')} {t('passengers')}, {t('reservations')}, {t('businesses')}...
                                </div>
                            )}

                            {displayData.length === 0 && query.length >= 2 && !isLoading && (
                                <div className="p-4 text-center text-xs text-gray-400 font-medium">
                                    {t('no_results_found')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}